import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
  AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Euro, Calendar, 
  ArrowUp, ArrowDown, DollarSign, Target
} from "lucide-react";

interface RevenueAnalysisProps {
  period: string;
}

const COLORS = {
  primary: '#3b82f6',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#06b6d4'
};

// Datos de ejemplo - en producción vendrían de la base de datos
const revenueData = [
  { month: 'Ene', revenue: 2100, target: 2500, growth: 5.2 },
  { month: 'Feb', revenue: 2350, target: 2500, growth: 11.9 },
  { month: 'Mar', revenue: 2200, target: 2500, growth: -6.4 },
  { month: 'Abr', revenue: 2450, target: 2500, growth: 11.4 },
  { month: 'May', revenue: 2600, target: 2500, growth: 6.1 },
  { month: 'Jun', revenue: 2380, target: 2500, growth: -8.5 }
];

const monthlyComparison = [
  { category: 'Enero', thisYear: 2100, lastYear: 1950, difference: 150 },
  { category: 'Febrero', thisYear: 2350, lastYear: 2180, difference: 170 },
  { category: 'Marzo', thisYear: 2200, lastYear: 2280, difference: -80 },
  { category: 'Abril', thisYear: 2450, lastYear: 2300, difference: 150 },
  { category: 'Mayo', thisYear: 2600, lastYear: 2400, difference: 200 },
  { category: 'Junio', thisYear: 2380, lastYear: 2320, difference: 60 }
];

const revenueBySource = [
  { name: 'Fútbol', value: 1200, percentage: 35 },
  { name: 'Baloncesto', value: 800, percentage: 23 },
  { name: 'Natación', value: 600, percentage: 18 },
  { name: 'Tenis', value: 450, percentage: 13 },
  { name: 'Otros', value: 380, percentage: 11 }
];

export const RevenueAnalysis = ({ period }: RevenueAnalysisProps) => {
  const currentRevenue = 14080;
  const previousRevenue = 13200;
  const growth = ((currentRevenue - previousRevenue) / previousRevenue) * 100;
  const targetRevenue = 15000;
  const targetAchievement = (currentRevenue / targetRevenue) * 100;

  return (
    <div className="space-y-6">
      {/* KPIs principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ingresos del Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-success">
                  €{currentRevenue.toLocaleString()}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  {growth >= 0 ? (
                    <ArrowUp className="w-4 h-4 text-success" />
                  ) : (
                    <ArrowDown className="w-4 h-4 text-destructive" />
                  )}
                  <span className={`text-sm font-medium ${growth >= 0 ? 'text-success' : 'text-destructive'}`}>
                    {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
                  </span>
                  <span className="text-sm text-muted-foreground">vs anterior</span>
                </div>
              </div>
              <div className="p-3 bg-success/10 rounded-full">
                <Euro className="w-6 h-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Cumplimiento Objetivo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-primary">
                  {targetAchievement.toFixed(1)}%
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  €{currentRevenue.toLocaleString()} / €{targetRevenue.toLocaleString()}
                </div>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <Target className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ingresos Promedio/Mes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-info">
                  €{Math.round(currentRevenue / 6).toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Últimos 6 meses
                </div>
              </div>
              <div className="p-3 bg-info/10 rounded-full">
                <Calendar className="w-6 h-6 text-info" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolución mensual */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Evolución Mensual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    `€${value}`,
                    name === 'revenue' ? 'Ingresos' : 'Objetivo'
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={COLORS.primary}
                  fill={COLORS.primary}
                  fillOpacity={0.6}
                />
                <Line
                  type="monotone"
                  dataKey="target"
                  stroke={COLORS.warning}
                  strokeDasharray="5 5"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Comparación anual */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart className="w-5 h-5 text-primary" />
              Comparación Anual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyComparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip formatter={(value) => `€${value}`} />
                <Bar dataKey="thisYear" fill={COLORS.primary} name="Este Año" />
                <Bar dataKey="lastYear" fill={COLORS.info} name="Año Anterior" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Ingresos por fuente */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Ingresos por Actividad
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={revenueBySource}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                >
                  {revenueBySource.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `€${value}`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Detalle por Actividad
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {revenueBySource.map((activity, index) => (
                <div key={activity.name} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: Object.values(COLORS)[index] }}
                    />
                    <span className="font-medium">{activity.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">€{activity.value}</div>
                    <Badge variant="outline" className="text-xs">
                      {activity.percentage}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de crecimiento mensual */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Crecimiento Mensual
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Mes</th>
                  <th className="text-right py-2 px-3 font-medium">Ingresos</th>
                  <th className="text-right py-2 px-3 font-medium">Objetivo</th>
                  <th className="text-right py-2 px-3 font-medium">Crecimiento</th>
                  <th className="text-center py-2 px-3 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.map((month, index) => (
                  <tr key={index} className="border-b hover:bg-muted/50">
                    <td className="py-2 px-3 font-medium">{month.month}</td>
                    <td className="py-2 px-3 text-right font-medium">€{month.revenue}</td>
                    <td className="py-2 px-3 text-right text-muted-foreground">€{month.target}</td>
                    <td className="py-2 px-3 text-right">
                      <span className={`font-medium ${month.growth >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {month.growth >= 0 ? '+' : ''}{month.growth}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <Badge variant={month.revenue >= month.target ? 'default' : 'secondary'}>
                        {month.revenue >= month.target ? 'Cumplido' : 'Pendiente'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RevenueAnalysis;